import React, { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  DOCKED_HEADER_HEIGHT,
  DOCKED_SIDEBAR_WIDTH,
  DOCKED_SIDEBAR_WIDTH_ICON,
} from "./Sidebar.jsx";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "./Collapsible.jsx";
import {
  LayoutDashboard,
  Users,
  FolderOpen,
  MessageSquare,
  UserPlus,
  UserCog,
  ChevronDown,
  Globe,
  AlertTriangle,

  Sparkles,
  UserRound,
  User,
  Search,
  PenTool,
  FileText,
  Repeat,
  BarChart3,
  Video,
  Clock,
  Building2,
  BookOpen,
  LockKeyhole,
} from "lucide-react";
import {
  getClientIntegrations,
  hasAnyIntegration,
} from "../utils/clientIntegrations";
import { SetupIcon } from "./icons/SetupIcon.jsx";
import { useAuth } from "./contexts/AuthContext.jsx";
import { UserBox } from "./UserBox.jsx";
import { TrialBanner } from "./TrialBanner.jsx";
import { BillingStatusBanner } from "./BillingStatusBanner.jsx";
import { AlertNote } from "./AlertNote.jsx";
import { ReadOnlyBadge } from "./ReadOnlyBadge.jsx";
import { CommercialTour } from "./CommercialTour.jsx";
import { isWelcomeTourPending } from "../Tutorials";
import { Avatar } from "./Avatar.jsx";
import { getAvatarImageUrl } from "../utils/avatarUtils";
import { Button } from "./Button.jsx";
import { Badge } from "./Badge.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./DropdownMenu.jsx";
import { useTheme } from "../theme/useTheme.js";
import { getData } from "./hooks/getData.js";
import apiClient from "../api-client.js";
import { NotificationMenu } from "./NotificationMenu.jsx";
import { Breadcrumb } from "./Breadcrumb.jsx";
import FloatingCopilotButton from "./FloatingCopilotButton.jsx";
import { FeaturebaseButton } from "./FeaturebaseButton.jsx";
import { SupportButton } from "./SupportButton.jsx";
import { AnnouncementDetailModal } from "../Pages/Dashboard/components/AnnouncementDetailModal.jsx";
import SuccessPopup from "./SuccessPopup.jsx";
import serseoImage from "../../serseo.jpeg";
import markezyLogo from "../../markezy-login-logo.png";
import {
  FEATURE_CLIENT_REPORTING,
  FEATURE_RECURRING_PROJECTS,
  FEATURE_SERSEO_CONTENT,
  hasAgencyFeature,
} from "@/utils/agencyFeatures";
import { SettingsSidebar } from "../Pages/Settings/SettingsSidebar.jsx";
import { isSettingsPath, rememberSettingsReturnPath, SETTINGS_BASE_PATH } from "../Pages/Settings/settingsPaths";
import { readOnlyClientMessage } from "@/utils/clientAccess";

const CONTENT_SECTION_PATH = "/HerramientasIA/contenidos";

const getSetupFlagValue = (value, fieldName) => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  if (value && typeof value === "object") {
    return getSetupFlagValue(
      value[fieldName] ??
      value.active ??
      value.is_active ??
      value.value ??
      value.status,
      fieldName
    );
  }
  return undefined;
};

// Reads a per-client setup flag from the /me/ profile. The backend sends a
// boolean for single-client users and a {client_id: boolean} map otherwise.
// `aggregate` decides how a multi-client map collapses when no client is
// selected: "some" (default) or "every" (used by flags that hide UI, so a
// single client still needing it keeps the section visible).
const getSelectedClientSetupFlag = (
  user,
  fieldName,
  clientId = null,
  aggregate = "some"
) => {
  if (!user || user.role !== "client") return undefined;

  const rawValue = user[fieldName];
  const hasClientId = clientId !== null && clientId !== undefined;

  if (typeof rawValue === "boolean") return rawValue;

  if (Array.isArray(rawValue)) {
    if (hasClientId) {
      const match = rawValue.find((entry) => {
        const entryId = entry?.id ?? entry?.client_id ?? entry?.clientId;
        return entryId !== undefined && entryId !== null
          ? String(entryId) === String(clientId)
          : false;
      });
      const matchValue = getSetupFlagValue(match, fieldName);
      if (typeof matchValue === "boolean") return matchValue;
    } else {
      const values = rawValue
        .map((entry) => getSetupFlagValue(entry, fieldName))
        .filter((value) => typeof value === "boolean");
      if (values.length) {
        return aggregate === "every"
          ? values.every(Boolean)
          : values.some(Boolean);
      }
    }
  }

  if (rawValue && typeof rawValue === "object") {
    if (hasClientId) {
      const key = String(clientId);
      if (Object.prototype.hasOwnProperty.call(rawValue, key)) {
        const matchValue = getSetupFlagValue(rawValue[key], fieldName);
        if (typeof matchValue === "boolean") return matchValue;
      }
    } else {
      const values = Object.values(rawValue)
        .map((entry) => getSetupFlagValue(entry, fieldName))
        .filter((value) => typeof value === "boolean");
      if (values.length) {
        return aggregate === "every"
          ? values.every(Boolean)
          : values.some(Boolean);
      }
    }
  }

  return undefined;
};

const getSelectedClientSetupActive = (user, clientId = null) =>
  getSelectedClientSetupFlag(user, "active_setup", clientId);

// True when the client's setup project exists but is marked "no necesario":
// nothing about the setup/itinerary should be offered to them.
const getSelectedClientSetupNotNeeded = (user, clientId = null) =>
  getSelectedClientSetupFlag(user, "setup_not_needed", clientId, "every");

const normalizeUnopenedAnnouncements = (value) => {
  if (!Array.isArray(value)) return [];

  const normalizedEntries = [];
  const seenAnnouncementIds = new Set();

  value.forEach((entry) => {
    if (typeof entry === "number" || typeof entry === "string") {
      const announcementId = Number(entry);
      if (Number.isFinite(announcementId) && announcementId > 0) {
        if (!seenAnnouncementIds.has(announcementId)) {
          seenAnnouncementIds.add(announcementId);
          normalizedEntries.push({
            announcementId,
            notificationId: null,
          });
        }
      }
      return;
    }

    if (typeof entry === "object" && entry !== null) {
      const announcementId = Number(
        entry.id ?? entry.announcement_id ?? entry.announcementId
      );
      const notificationId = Number(
        entry.notification_id ?? entry.notificationId ?? entry.notification?.id
      );
      if (Number.isFinite(announcementId) && announcementId > 0) {
        if (!seenAnnouncementIds.has(announcementId)) {
          seenAnnouncementIds.add(announcementId);
          normalizedEntries.push({
            announcementId,
            notificationId:
              Number.isFinite(notificationId) && notificationId > 0
                ? notificationId
                : null,
          });
        }
      }
    }
  });

  return normalizedEntries;
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const deepMerge = (base, patch) => {
  if (!isPlainObject(base) || !isPlainObject(patch)) {
    return patch;
  }

  const merged = { ...base };

  Object.entries(patch).forEach(([key, value]) => {
    if (isPlainObject(value) && isPlainObject(base[key])) {
      merged[key] = deepMerge(base[key], value);
      return;
    }

    merged[key] = value;
  });

  return merged;
};

const renderAppHeader = ({
  pageContext,
  user,
  notificationsCount,
  onConversationNotificationClick,
  theme,
  neutrals,
  isMobile,
  agencyLogoUrl,
}) => (
  <Box
    component="header"
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1002,
      display: "flex",
      height: DOCKED_HEADER_HEIGHT,
      alignItems: "center",
      gap: { xs: 0.5, md: 2 },
      borderBottom: `1px solid ${neutrals.gray[200]}`,
      padding: { xs: "0 12px 0 0", md: "0 36px 0 0" },
      backgroundColor: theme.palette.background.paper,
      boxSizing: "border-box",
    }}
  >
    {/* Logo fuera del sidebar: no se comprime al contraer la barra lateral */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: { xs: "auto", md: DOCKED_SIDEBAR_WIDTH },
        padding: { xs: "0 4px 0 8px", md: "0 12px" },
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      <SidebarTrigger />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: agencyLogoUrl ? { xs: 140, md: "none" } : { xs: 116, md: 150 },
          height: agencyLogoUrl ? { xs: 32, md: 46 } : { xs: 24, md: 31 },
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          component="img"
          src={agencyLogoUrl || markezyLogo}
          alt={agencyLogoUrl ? "Logotipo de la agencia" : "Markezy — Tu agencia IA Ready. En un solo lugar"}
          sx={{
            height: "100%",
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            objectPosition: "left center",
          }}
        />
      </Box>
    </Box>

    {pageContext.breadcrumbItems && pageContext.breadcrumbItems.length > 0 && (
      <Box sx={{ marginLeft: { xs: 0, md: "8px" }, minWidth: 0 }}>
        <Breadcrumb items={pageContext.breadcrumbItems} compact={isMobile} />
      </Box>
    )}

    <Box sx={{ flex: 1 }} />

    <TrialBanner />

    <SupportButton />

    {user?.role !== "freelance_ticket" && <FeaturebaseButton />}

    <NotificationMenu
      notificationsCount={notificationsCount}
      onConversationNotificationClick={onConversationNotificationClick}
    />

    <FloatingCopilotButton variant="header" />
  </Box>
);

// Items del menú que resalta el recorrido de bienvenida (Tutorials/WelcomeSidebarTour)
const WELCOME_TOUR_TARGETS = {
  clients: "clients",
  analytics: "analytics",
  projects: "projects",
  conversations: "conversations",
  contenidos: "contents",
  informes: "reports",
  "section-agencia": "agency",
  "internal-management": "agency",
  team: "agency",
};

const getMenuItems = (
  userRole,
  unreadIncidentsCount,
  clientProjectsMenuConfig = {},
  user = null,
  selectedClientSetupActive = undefined,
  selectedClientSetupNotNeeded = undefined,
  selectedClientId = null
) => {
  // Sin recurrentes solo quedarían los personalizados: Proyectos pasa a ser enlace directo
  const showProjectSubmenu =
    userRole !== "client" && hasAgencyFeature(user, FEATURE_RECURRING_PROJECTS);
  const items = [
    {
      id: "dashboard",
      title: userRole === "freelance_ticket" ? "Notificaciones" : "Dashboard",
      icon: LayoutDashboard,
      roles: ["superadmin", "agency_admin", "consultant", "client", "freelance_ticket"],
      path: "/dashboard",
    },
    {
      id: "clients",
      title: "Clientes",
      icon: Users,
      roles: ["superadmin", "agency_admin", "consultant"],
      path: "/clients",
    },
    {
      id: "analytics",
      title: "Analíticas",
      icon: BarChart3,
      roles: ["superadmin", "agency_admin", "consultant"],
      path: "/analiticas",
    },
    {
      id: "projects",
      title: "Proyectos",
      icon: FolderOpen,
      roles: ["superadmin", "agency_admin", "consultant", "client"],
      isCollapsible: showProjectSubmenu,
      path: showProjectSubmenu ? undefined : "/projects/custom",
      subItems: showProjectSubmenu
        ? [
          {
            id: "projects-custom",
            title: "Personalizados",
            icon: FolderOpen,
            path: "/projects/custom",
          },
          {
            id: "projects-recurrent",
            title: "Recurrentes",
            icon: Repeat,
            path: "/projects/recurrent",
          },
        ]
        : undefined,
    },
    {
      id: "conversations",
      title: userRole === "client" ? "Comunicación" : "Conversaciones",
      icon: MessageSquare,
      roles: ["superadmin", "agency_admin", "consultant", "client"],
      path: "/conversations",
    },
    {
      id: "leads",
      title: "Leads",
      icon: User,
      roles:
        userRole === "superadmin"
          ? ["superadmin"]
          : ["agency_admin", "consultant"],
      isCollapsible: true,
      subItems:
        userRole === "superadmin"
          ? [
            {
              id: "leads-clientify",
              title: "Clientify",
              icon: User,
              path: "/leads/clientify",
            },
            {
              id: "leads-serseo",
              title: "Serseo",
              icon: UserRound,
              path: "/leads/serseo",
            },
            {
              id: "leads-planes",
              title: "Planes",
              icon: Sparkles,
              path: "/HerramientasIA/planes",
              badge: "beta",
            },
          ]
          : [
            ...(user?.clientify_id === true
              ? [{
                id: "leads-clientify",
                title: "Clientify",
                icon: User,
                path: "/leads/clientify",
              }]
              : []),
            {
              id: "leads-planes",
              title: "Planes",
              icon: Sparkles,
              path: "/HerramientasIA/planes",
              badge: "beta",
            },
          ],
    },
    {
      id: "incidents",
      title: "Tickets",
      icon: AlertTriangle,
      roles: ["superadmin", "agency_admin", "consultant", "freelance_ticket"],
      badge: unreadIncidentsCount > 0 ? unreadIncidentsCount : undefined,
      path: "/tickets",
    },
    {
      id: "team",
      title: userRole === "superadmin" ? "Agencias" : "Equipo",
      icon: UserCog,
      roles: ["superadmin", "agency_admin"],
      path: "/team",
    },
    {
      id: "internal-management",
      title: "Gestión interna",
      icon: Building2,
      roles: ["agency_admin", "consultant"],
      isCollapsible: true,
      subItems: [
        {
          id: "internal-communication",
          title: "Canal interno",
          icon: MessageSquare,
          path: "/gestion-interna/comunicacion",
        },
        {
          id: "internal-documentation",
          title: "Documentación",
          icon: BookOpen,
          path: "/gestion-interna/documentacion",
        },
      ],
    },
    {
      id: "onboarding",
      title: "Onboarding",
      icon: UserPlus,
      roles: ["client"],
      path: "/onboarding",
    },
    {
      id: "contenidos",
      title: "Contenidos",
      icon: PenTool,
      roles: ["superadmin", "agency_admin", "consultant"],
      path: "/HerramientasIA/contenidos",
      badge: "beta",
    },
    {
      id: "informes",
      title: "Informes",
      icon: FileText,
      roles: ["superadmin", "agency_admin", "consultant"],
      path: "/HerramientasIA/informes",
      badge: "beta",
    },
    {
      id: "setup",
      title: userRole === "client" ? "Itinerario" : "Setups",
      icon: SetupIcon,
      roles: ["superadmin", "agency_admin", "consultant", "client"],
      path: "/setup",
      // Hide setup for clients without a hired pack, and also for those whose
      // setup is explicitly marked as "no necesario"
      hidden:
        userRole === "client" &&
        (selectedClientSetupActive === false ||
          selectedClientSetupNotNeeded === true),
    },
    {
      id: "content",
      title: "Contenidos",
      icon: PenTool,
      roles: ["client"],
      path: "/contenido",
      // Hide content for client users without content generator config
      hidden: userRole === "client" && user?.has_content_config === false,
    },
    {
      id: "reports",
      title: "Informes",
      icon: FileText,
      roles: ["client"],
      path: "/informes",
      hidden: !hasAgencyFeature(user, FEATURE_CLIENT_REPORTING),
    },
    {
      id: "metrics",
      title: "Métricas",
      icon: BarChart3,
      roles: ["client"],
      path: "/metricas",
      // Only on plans with client reporting and when the selected company has GA4, Search Console or SE Ranking linked
      hidden:
        userRole === "client" &&
        (!hasAgencyFeature(user, FEATURE_CLIENT_REPORTING) ||
          !hasAnyIntegration(getClientIntegrations(user, selectedClientId).flags)),
    },
    {
      id: "zona-serseo",
      title: "Zona Serseo",
      icon: Globe,
      roles: ["superadmin", "agency_admin", "consultant"],
      path: "/Portal/ZonaSerseo",
      hidden: !hasAgencyFeature(user, FEATURE_SERSEO_CONTENT),
    },
    {
      id: "academia",
      title: "Academia",
      icon: Video,
      roles: ["superadmin", "agency_admin", "consultant"],
      path: "/Portal/Academia",
      hidden: !hasAgencyFeature(user, FEATURE_SERSEO_CONTENT),
    },
    {
      id: "logs",
      title: "Logs",
      icon: Clock,
      roles: ["superadmin"],
      path: "/Portal/Logs",
    },
  ];

  if (userRole === "client") {
    const projectsItem = items.find((item) => item.id === "projects");
    if (projectsItem) {
      if (clientProjectsMenuConfig.title) {
        projectsItem.title = clientProjectsMenuConfig.title;
      }
      projectsItem.hidden = Boolean(clientProjectsMenuConfig.isHidden);
    }
  }


  return items;
};

// Componente para el header de la página
export const PageHeader = ({
  title,
  subtitle,
  showTitle = true,
  leftContent = null,
  rightContent = null,
  titleRightContent = null, // Nuevo prop para contenido a la derecha del título
}) => {
  const { theme, spacing } = useTheme();

  if (!showTitle || !title) return null;

  return (
    <Box sx={{ marginBottom: "12px" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: "8px 12px", sm: "16px" },
          minHeight: "36px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          {leftContent && (
            <Box
              sx={{ display: "flex", alignItems: "center", marginRight: "4px" }}
            >
              {leftContent}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.375rem" },
                color: theme.palette.text.primary,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {titleRightContent && (
              <Box sx={{ marginLeft: 2 }}>{titleRightContent}</Box>
            )}
          </Box>
        </Box>
        {rightContent && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            {rightContent}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export function AppLayout() {
  const {
    user,
    logout,
    interceptorReady,
    refreshUser,
  } = useAuth();
  const { theme, colors, neutrals, getRoleBranding } = useTheme();
  const agencyLogoUrl = user?.agencyBranding?.logo_url;
  const navigate = useNavigate();
  const location = useLocation();
  // Ajustes: el sidebar principal queda en iconos y al lado va el menú de Ajustes;
  const isSettingsRoute = isSettingsPath(location.pathname);
  // The content section keeps its layout (guided tutorial) mounted across its own routes;
  // ContentTutorialLayout remounts each page by pathname itself.
  const outletKey =
    location.pathname === CONTENT_SECTION_PATH || location.pathname.startsWith(`${CONTENT_SECTION_PATH}/`)
      ? CONTENT_SECTION_PATH
      : isSettingsRoute
        ? SETTINGS_BASE_PATH
        : location.pathname;
  const roleColors = getRoleBranding(user?.role);
  const [openCollapsibles, setOpenCollapsibles] = React.useState(new Set());

  // Estado del sidebar: comprimido = solo iconos (no se oculta del todo)
  const muiTheme = useMuiTheme();
  const isMobileViewport = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = React.useState(
    () => localStorage.getItem("sidebar_state") !== "false"
  );
  const isSidebarCollapsed = (!sidebarOpen || isSettingsRoute) && !isMobileViewport;
  const showSettingsSidebar = isSettingsRoute && !isMobileViewport;

  const withCollapsedTooltip = (node, title, key) =>
    isSidebarCollapsed ? (
      <Tooltip key={key} title={title} placement="right" arrow>
        {node}
      </Tooltip>
    ) : (
      node
    );

  const [selectedFilterData, setSelectedFilterData] = React.useState(() => {
    const stored = localStorage.getItem("selectedFilterData");
    if (stored) {
      const parsed = JSON.parse(stored);
      return { config_id: null, ...parsed };
    }
    return { id: "global", name: "Vista Global", config_id: null };
  });

  const selectedFilter = selectedFilterData.id;

  // Guardar en localStorage
  React.useEffect(() => {
    localStorage.setItem(
      "selectedFilterData",
      JSON.stringify(selectedFilterData)
    );
  }, [selectedFilterData]);

  const [pageContext, setPageContext] = React.useState({ showTitle: true });
  const [refreshFilterKey, setRefreshFilterKey] = React.useState(0); // key para forzar recarga del endpoint del filtro
  const [userRefreshKey, setUserRefreshKey] = React.useState(0); // key para forzar recarga del perfil de usuario
  const [dashboardRefreshKey, setDashboardRefreshKey] = React.useState(0);
  const [clientsRefreshKey, setClientsRefreshKey] = React.useState(0);
  const [clientDetailRefreshKey, setClientDetailRefreshKey] = React.useState(0);
  const [teamRefreshKey, setTeamRefreshKey] = React.useState(0);
  const [projectsRefreshKey, setProjectsRefreshKey] = React.useState(0);
  const [onboardingRefreshKey, setOnboardingRefreshKey] = React.useState(0);
  const [conversationsRefreshKey, setConversationsRefreshKey] =
    React.useState(0);
  const [
    conversationsNotificationsRefreshKey,
    setConversationsNotificationsRefreshKey,
  ] = React.useState(0);
  const [incidentsRefreshKey, setIncidentsRefreshKey] = React.useState(0);
  const [technicalSetupRefreshKey, setTechnicalSetupRefreshKey] =
    React.useState(0);
  const [filterRefresh, setFilterRefresh] = useState(0);
  const [leadsRefreshKey, setLeadsRefreshKey] = React.useState(0);
  const [contentGeneratorRefreshKey, setContentGeneratorRefreshKey] = React.useState(0);
  const [marketingPlanRefresh, setMarketingPlanRefresh] = React.useState(0);
  const [reportsRefreshKey, setReportsRefreshKey] = React.useState(0);
  const [filterSearchQuery, setFilterSearchQuery] = React.useState("");

  // Función para actualizar las preferencias de la interfaz de usuario, ya que la usaremos en varias páginas.
  const updateUiPreferences = React.useCallback(async (preferencesPatch) => {
    if (!user) {
      throw new Error("No authenticated user");
    }

    const nextPreferences = deepMerge(user.ui_preferences || {}, preferencesPatch || {});
    const response = await apiClient.patch("users/me/", {
      ui_preferences: nextPreferences,
    });

    await refreshUser();

    if (response?.data) {
      return response.data.ui_preferences || {};
    }

    return nextPreferences;
  }, [refreshUser, user]);

  // Función para obtener el setup activo del cliente
  const selectedClientSetupActive = React.useMemo(() => {
    const clientId =
      selectedFilterData.id === "global" ? null : selectedFilterData.id;
    return getSelectedClientSetupActive(user, clientId);
  }, [selectedFilterData.id, user]);

  // El setup marcado como "no necesario" se oculta por completo al cliente
  const selectedClientSetupNotNeeded = React.useMemo(() => {
    const clientId =
      selectedFilterData.id === "global" ? null : selectedFilterData.id;
    return getSelectedClientSetupNotNeeded(user, clientId);
  }, [selectedFilterData.id, user]);

  // Contador para saber si es la primera carga de cada página
  const isFirstMount = React.useRef({
    dashboard: true,
    clients: true,
    team: true,
    projects: true,
    onboarding: true,
    conversations: true,
    incidents: true,
    technicalSetup: true,
    user: true,
    contentGenerator: true,
    marketingPlan: true,
    reports: true,
  });

  // Referencia para trackear el pathname anterior
  const previousPathnameRef = React.useRef(null);


  // Función para refrescar datos del filtro
  const refreshFilterData = React.useCallback(
    () => setRefreshFilterKey((prev) => prev + 1),
    []
  );

  // Función para refrescar notificaciones de conversaciones
  const refreshConversationsNotifications = React.useCallback(
    () => setConversationsNotificationsRefreshKey((prev) => prev + 1),
    []
  );

  // Scroll al top en cambio de ruta y manejo de refresh keys
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    // Trackear el pathname anterior
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    // Incrementar refresh key correspondiente solo en re-visitas (no primera carga)
    if (location.pathname === "/dashboard") {
      if (isFirstMount.current.dashboard) {
        isFirstMount.current.dashboard = false;
      } else {
        setDashboardRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname === "/clients") {
      isFirstMount.current.clients = false;
      setClientsRefreshKey((prev) => prev + 1);
    } else if (location.pathname === "/team") {
      if (isFirstMount.current.team) {
        isFirstMount.current.team = false;
      } else {
        setTeamRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname === "/onboarding") {
      if (isFirstMount.current.onboarding) {
        isFirstMount.current.onboarding = false;
      } else {
        setOnboardingRefreshKey((prev) => prev + 1);
      }
    } else if (
      location.pathname === "/projects/custom" ||
      location.pathname === "/projects/recurrent"
    ) {
      if (isFirstMount.current.projects) {
        isFirstMount.current.projects = false;
      } else {
        setProjectsRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname.startsWith("/leads")) {
      if (isFirstMount.current.leads) {
        isFirstMount.current.leads = false;
      } else {
        setLeadsRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname === "/conversations") {
      if (isFirstMount.current.conversations) {
        isFirstMount.current.conversations = false;
      } else {
        // Solo incrementar si NO viene de una página de conversations
        if (
          !previousPathname ||
          !previousPathname.startsWith("/conversations")
        ) {
          setConversationsRefreshKey((prev) => prev + 1);
        }
      }
    } else if (location.pathname === "/tickets") {
      if (isFirstMount.current.incidents) {
        isFirstMount.current.incidents = false;
      } else {
        setIncidentsRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname === "/setup") {
      if (isFirstMount.current.technicalSetup) {
        isFirstMount.current.technicalSetup = false;
      } else {
        setTechnicalSetupRefreshKey((prev) => prev + 1);
      }
    } else if (isSettingsPath(location.pathname)) {
      // Solo al entrar en Ajustes: cambiar de sección dentro no cuenta
      if (!previousPathname || !isSettingsPath(previousPathname)) {
        if (previousPathname) rememberSettingsReturnPath(previousPathname);
        if (isFirstMount.current.user) {
          isFirstMount.current.user = false;
        } else {
          setUserRefreshKey((prev) => prev + 1);
        }
      }
    } else if (location.pathname === "/HerramientasIA/contenidos") {
      if (isFirstMount.current.contentGenerator) {
        isFirstMount.current.contentGenerator = false;
      } else {
        setContentGeneratorRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname === "/HerramientasIA/planes") {
      if (isFirstMount.current.marketingPlan) {
        isFirstMount.current.marketingPlan = false;
      } else {
        setMarketingPlanRefresh((prev) => prev + 1);
      }
    } else if (location.pathname === "/contenido") {
      if (isFirstMount.current.contentGenerator) {
        isFirstMount.current.contentGenerator = false;
      } else {
        setContentGeneratorRefreshKey((prev) => prev + 1);
      }
    } else if (location.pathname === "/HerramientasIA/informes") {
      if (isFirstMount.current.reports) {
        isFirstMount.current.reports = false;
      } else {
        setReportsRefreshKey((prev) => prev + 1);
      }
    }
  }, [location.pathname]); // Obtener endpoint según el rol del usuario
  const getFilterDataEndpoint = () => {
    if (user?.role === "superadmin") {
      return "agencies/";
    } else if (user?.role === "agency_admin") {
      return `clients/options/?agency=${user.agencyId}&ordering=name`;
    } else if (user?.role === "consultant") {
      return `clients/options/?consultant=${user.id}&ordering=name`;
    }
    return null;
  };

  // Cargar datos para el filtro desde backend
  const { data: filterData } = getData(
    getFilterDataEndpoint(),
    interceptorReady &&
    (user?.role === "superadmin" ||
      user?.role === "agency_admin" ||
      user?.role === "consultant"),
    [user?.id, user?.agencyId, refreshFilterKey]
  );

  const clientFilterOptions = React.useMemo(() => {
    if (user?.role !== "client") {
      return [];
    }

    return Array.isArray(user?.client) ? user.client : [];
  }, [user?.client, user?.role]);

  const shouldShowClientFilter =
    user?.role === "client" && clientFilterOptions.length > 1;

  const filterOptions = React.useMemo(() => {
    if (user?.role === "superadmin") {
      if (!filterData) return [];
      // Para superadmin: mostrar agencias
      return Array.isArray(filterData) ? filterData : [];
    } else if (user?.role === "agency_admin" || user?.role === "consultant") {
      if (!filterData) return [];
      // Para agency_admin y consultant: mostrar clientes asociados
      const clients = filterData?.results || filterData || [];
      return Array.isArray(clients)
        ? clients.map((client) => ({
          ...client,
          config_id: client.config_id ?? null,
        }))
        : [];
    } else if (user?.role === "client") {
      return clientFilterOptions;
    }
    return [];
  }, [clientFilterOptions, filterData, user?.role]);

  const clientProjectsMenuConfig = React.useMemo(() => {
    if (user?.role !== "client") {
      return {};
    }
    if (user?.has_projects === undefined) {
      return {};
    }

    if (!user?.has_projects) {
      return { isHidden: true };
    }

    return {
      title: "Proyectos",
      isHidden: false,
    };
  }, [user?.has_projects, user?.role]);

  React.useEffect(() => {
    if (
      user?.role === "client" &&
      user?.has_projects === false &&
      location.pathname.startsWith("/projects")
    ) {
      navigate("/dashboard", { replace: true });
    }
  }, [
    location.pathname,
    navigate,
    user?.has_projects,
    user?.role,
  ]);

  // Función para cambiar el filtro
  const changeSelectedFilter = React.useCallback(
    (id) => {
      let didChange = false;

      setSelectedFilterData((prev) => {
        if (prev.id === id) {
          return prev;
        }

        didChange = true;

        if (id === "global") {
          return { id: "global", name: "Vista Global", config_id: null };
        }

        const item = filterOptions.find((opt) => opt.id === id);
        if (item) {
          return {
            id: item.id,
            name: item.name,
            config_id: item.config_id ?? null,
          };
        }

        didChange = false;
        return prev;
      });

      if (didChange) {
        setFilterRefresh((prev) => prev + 1);
      }
    },
    [filterOptions]
  );

  // Verificar si el selectedFilter existe en filterOptions, si no, resetear a global
  React.useEffect(() => {
    if (user?.role === "client") {
      if (selectedFilter !== "global") {
        const exists = clientFilterOptions.some(
          (item) => item.id === selectedFilter
        );
        if (!exists) {
          setSelectedFilterData({ id: "global", name: "Vista Global", config_id: null });
        }
      }
      return;
    }

    if (filterOptions.length > 0 && selectedFilter !== "global") {
      const exists = filterOptions.some((item) => item.id === selectedFilter);
      if (!exists) {
        setSelectedFilterData({ id: "global", name: "Vista Global", config_id: null });
      }
    }
  }, [clientFilterOptions, filterOptions, selectedFilter, user?.role]);

  React.useEffect(() => {
    if (selectedFilterData.id === "global") {
      if (selectedFilterData.config_id !== null) {
        setSelectedFilterData({ id: "global", name: "Vista Global", config_id: null });
      }
      return;
    }

    const match = filterOptions.find((item) => item.id === selectedFilterData.id);
    if (!match) return;

    const nextConfigId = match.config_id ?? null;

    if (selectedFilterData.config_id !== nextConfigId) {
      setSelectedFilterData((prev) => ({
        ...prev,
        config_id: nextConfigId,
      }));
    }
  }, [filterOptions, selectedFilterData.id, selectedFilterData.config_id]);

  // Superadmin filters by agency; the rest filter by client and get its avatar.
  const showClientAvatars = user?.role !== "superadmin";
  const selectedFilterOption = React.useMemo(
    () =>
      selectedFilterData.id === "global"
        ? null
        : filterOptions.find((item) => item.id === selectedFilterData.id) ?? null,
    [filterOptions, selectedFilterData.id]
  );

  // Estado para el contador de notificaciones del dashboard
  const [notificationsCount, setNotificationsCount] = React.useState(0);
  const [notificationsRefreshKey, setNotificationsRefreshKey] =
    React.useState(0);

  // Obtener el conteo de notificaciones desde el endpoint de notificaciones
  const { data: notificationsData } = getData(
    "dashboard/notifications/?is_read=false&page_size=1",
    interceptorReady && !!user,
    [notificationsRefreshKey, user?.id]
  );

  // Actualizar el contador cuando lleguen los datos
  React.useEffect(() => {
    if (notificationsData?.count !== undefined) {
      setNotificationsCount(notificationsData.count);
    }
  }, [notificationsData]);

  // Polling cada 10 minutos para actualizar el contador de notificaciones
  React.useEffect(() => {
    if (interceptorReady && user) {
      const interval = setInterval(() => {
        setNotificationsRefreshKey((prev) => prev + 1);
      }, 10 * 60 * 1000); // 10 minutos = 600000 ms

      return () => clearInterval(interval);
    }
  }, [interceptorReady, user]);

  const unopenedAnnouncements = React.useMemo(
    () => normalizeUnopenedAnnouncements(user?.unopened_announcements),
    [user?.unopened_announcements]
  );

  const unopenedAnnouncementIds = React.useMemo(
    () => unopenedAnnouncements.map((entry) => entry.announcementId),
    [unopenedAnnouncements]
  );

  const unopenedAnnouncementNotificationIds = React.useMemo(() => {
    const ids = unopenedAnnouncements
      .map((entry) => entry.notificationId)
      .filter((id) => Number.isFinite(id) && id > 0);
    return Array.from(new Set(ids));
  }, [unopenedAnnouncements]);

  const unopenedAnnouncementIdsKey = React.useMemo(
    () => unopenedAnnouncementIds.join(","),
    [unopenedAnnouncementIds]
  );

  const [closedAnnouncementsKey, setClosedAnnouncementsKey] =
    React.useState("");
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] =
    React.useState(false);
  const [announcementCarouselIndex, setAnnouncementCarouselIndex] =
    React.useState(0);
  const [viewedAnnouncementIds, setViewedAnnouncementIds] = React.useState([]);
  const [announcementCloseErrorPopupOpen, setAnnouncementCloseErrorPopupOpen] =
    React.useState(false);
  const [pendingAnnouncementsResponse, setPendingAnnouncementsResponse] =
    React.useState([]);
  const [isPendingAnnouncementsLoading, setIsPendingAnnouncementsLoading] =
    React.useState(false);

  React.useEffect(() => {
    if (!user?.id) {
      setIsAnnouncementModalOpen(false);
      setClosedAnnouncementsKey("");
      setAnnouncementCarouselIndex(0);
      setViewedAnnouncementIds([]);
      return;
    }
  }, [user?.id]);

  React.useEffect(() => {
    let cancelled = false;

    const fetchPendingAnnouncements = async () => {
      if (!interceptorReady || unopenedAnnouncementIds.length === 0) {
        if (!cancelled) {
          setPendingAnnouncementsResponse([]);
          setIsPendingAnnouncementsLoading(false);
        }
        return;
      }

      setIsPendingAnnouncementsLoading(true);
      const announcements = [];

      for (const announcementId of unopenedAnnouncementIds) {
        try {
          const response = await apiClient.get(
            `communication/announcements/${announcementId}/`,
            { withCredentials: true }
          );
          if (response?.data) {
            announcements.push(response.data);
          }
        } catch (error) {
          console.error(
            `Error al obtener el anuncio ${announcementId}:`,
            error
          );
        }
      }

      if (!cancelled) {
        setPendingAnnouncementsResponse(announcements);
        setIsPendingAnnouncementsLoading(false);
      }
    };

    fetchPendingAnnouncements();

    return () => {
      cancelled = true;
    };
  }, [interceptorReady, unopenedAnnouncementIdsKey, user?.id]);

  const pendingAnnouncementsById = React.useMemo(() => {
    const map = new Map();
    (pendingAnnouncementsResponse || []).forEach((announcement) => {
      if (!announcement) return;
      const announcementId = announcement?.id ?? announcement?.pk;
      if (announcementId !== null && announcementId !== undefined) {
        map.set(String(announcementId), announcement);
      }
    });
    return map;
  }, [pendingAnnouncementsResponse]);

  const orderedPendingAnnouncements = React.useMemo(() => {
    return unopenedAnnouncementIds
      .map((announcementId) => pendingAnnouncementsById.get(String(announcementId)))
      .filter(Boolean);
  }, [pendingAnnouncementsById, unopenedAnnouncementIds]);

  const hasViewedAllAnnouncements = React.useMemo(() => {
    if (orderedPendingAnnouncements.length <= 1) return true;
    const viewedSet = new Set(viewedAnnouncementIds.map(String));
    return orderedPendingAnnouncements.every((announcement) => {
      const announcementId = announcement?.id ?? announcement?.pk;
      return (
        announcementId !== null &&
        announcementId !== undefined &&
        viewedSet.has(String(announcementId))
      );
    });
  }, [orderedPendingAnnouncements, viewedAnnouncementIds]);

  const handleCloseAnnouncementsCarousel = React.useCallback(async () => {
    if (
      orderedPendingAnnouncements.length > 1 &&
      !hasViewedAllAnnouncements
    ) {
      setAnnouncementCloseErrorPopupOpen(true);
      return;
    }

    setIsAnnouncementModalOpen(false);
    setClosedAnnouncementsKey(unopenedAnnouncementIdsKey);
    setViewedAnnouncementIds([]);

    if (unopenedAnnouncementNotificationIds.length > 0) {
      try {
        await apiClient.post(
          "dashboard/notifications/mark-opened/",
          { ids: unopenedAnnouncementNotificationIds },
          { withCredentials: true }
        );
        // The cached user in localStorage still lists them; a reload would reopen the modal
        await refreshUser();
        setNotificationsRefreshKey((prev) => prev + 1);
      } catch (error) {
        console.error("Error al marcar anuncios como abiertos:", error);
      }
    }
  }, [
    hasViewedAllAnnouncements,
    orderedPendingAnnouncements.length,
    refreshUser,
    unopenedAnnouncementNotificationIds,
    unopenedAnnouncementIdsKey,
  ]);

  const welcomeTourPending = isWelcomeTourPending(user);

  React.useEffect(() => {
    if (welcomeTourPending) return;
    if (isPendingAnnouncementsLoading) return;
    if (!unopenedAnnouncementIdsKey) return;
    if (closedAnnouncementsKey === unopenedAnnouncementIdsKey) return;
    if (orderedPendingAnnouncements.length === 0) return;

    setAnnouncementCarouselIndex(0);
    const firstAnnouncementId =
      orderedPendingAnnouncements[0]?.id ?? orderedPendingAnnouncements[0]?.pk;
    setViewedAnnouncementIds(
      firstAnnouncementId !== null && firstAnnouncementId !== undefined
        ? [String(firstAnnouncementId)]
        : []
    );
    setIsAnnouncementModalOpen(true);
  }, [
    closedAnnouncementsKey,
    isPendingAnnouncementsLoading,
    orderedPendingAnnouncements.length,
    orderedPendingAnnouncements,
    unopenedAnnouncementIdsKey,
    welcomeTourPending,
  ]);

  React.useEffect(() => {
    if (!isAnnouncementModalOpen || orderedPendingAnnouncements.length <= 1) {
      return;
    }

    const currentAnnouncement =
      orderedPendingAnnouncements[announcementCarouselIndex];
    const currentAnnouncementId =
      currentAnnouncement?.id ?? currentAnnouncement?.pk;

    if (currentAnnouncementId === null || currentAnnouncementId === undefined) {
      return;
    }

    setViewedAnnouncementIds((prevIds) => {
      const normalizedId = String(currentAnnouncementId);
      if (prevIds.includes(normalizedId)) return prevIds;
      return [...prevIds, normalizedId];
    });
  }, [
    announcementCarouselIndex,
    isAnnouncementModalOpen,
    orderedPendingAnnouncements,
  ]);

  // Ya no se consulta el endpoint de tickets al cargar la app
  const unreadIncidentsCount = 0;
  const menuItems = React.useMemo(
    () =>
      getMenuItems(
        user.role,
        unreadIncidentsCount,
        clientProjectsMenuConfig,
        user,
        selectedClientSetupActive,
        selectedClientSetupNotNeeded,
        selectedFilterData.id === "global" ? null : selectedFilterData.id
      ),
    [
      selectedFilterData.id,
      clientProjectsMenuConfig,
      unreadIncidentsCount,
      user.role,
      user,
      selectedClientSetupActive,
      selectedClientSetupNotNeeded,
    ]
  );

  // Auto-abrir el menú colapsable si estamos en una de sus sub-páginas
  React.useEffect(() => {
    const isCurrentPath = (path) =>
      !!path && (location.pathname === path || location.pathname.startsWith(path + "/"));

    setOpenCollapsibles((prev) => {
      const newSet = new Set(prev);
      menuItems.forEach((item) => {
        if (!item.isCollapsible || !item.subItems) return;
        const paths = item.subItems.map((subItem) => subItem.path);
        // Plantillas no tiene entrada propia en el menú pero cuelga de Proyectos
        if (item.id === "projects") paths.push("/projects/templates");
        if (paths.some(isCurrentPath)) {
          newSet.add(item.id);
        } else {
          newSet.delete(item.id);
        }
      });
      return newSet;
    });
  }, [location.pathname, menuItems]);

  const availableMenuItems = React.useMemo(
    () =>
      menuItems.filter(
        (item) => item.roles.includes(user.role) && item.hidden !== true
      ),
    [menuItems, user.role]
  );

  // Crear variable para almacenar los IDs de clientes para agency_admin y consultant
  const Clientes_prop = React.useMemo(() => {
    if (user?.role === "agency_admin" || user?.role === "consultant") {
      const clients = filterData?.results || filterData || [];
      return Array.isArray(clients) ? clients.map((client) => client.id) : [];
    }
    return [];
  }, [filterData, user?.role]);

  // Extraer los IDs de clientes del currentUser (para usuarios con role "client")
  const clientIds = React.useMemo(() => {
    if (!user) return [];
    if (Array.isArray(user.client)) {
      return user.client
        .map((client) => client?.id)
        .filter((clientId) => clientId !== undefined && clientId !== null);
    }
    return user.client ? [user.client] : [];
  }, [user]);

  // Flag para indicar si el usuario tiene múltiples clientes (reutilizable en todas las páginas)
  const isMultiClientUser = user?.role === "client" && clientIds.length > 1;

  // Obtener ruta actual para item activo
  const currentPath = location.pathname;
  const projectsSourcePath = React.useMemo(() => {
    if (currentPath !== "/projects/templates") {
      return null;
    }

    const from = new URLSearchParams(location.search).get("from");
    if (from === "recurrent") {
      return "/projects/recurrent";
    }
    if (from === "custom") {
      return "/projects/custom";
    }
    return null;
  }, [currentPath, location.search]);
  const isSubItemPathActive = React.useCallback(
    (itemId, subItemPath) => {
      if (
        itemId === "projects" &&
        currentPath === "/projects/templates" &&
        projectsSourcePath
      ) {
        return subItemPath === projectsSourcePath;
      }

      return currentPath === subItemPath || currentPath.startsWith(subItemPath + "/");
    },
    [currentPath, projectsSourcePath]
  );

  // Agrupar los items del menú en secciones (Cartera / Operación / Agencia / Serseo)
  const MENU_SECTIONS = [
    { id: "main", title: null, itemIds: ["dashboard"] },
    { id: "cartera", title: "Cartera", itemIds: ["clients","analytics", "projects", "leads"] },
    {
      id: "operacion",
      title: "Operación",
      itemIds: ["conversations", "incidents", "contenidos", "informes", "setup"],
    },
    { id: "agencia", title: "Agencia", itemIds: ["internal-management", "team"] },
    { id: "serseo", title: null, isSerseo: true, itemIds: ["zona-serseo", "academia", "logs"] },
  ];

  const { menuSections, showSectionHeaders } = React.useMemo(() => {
    // Las secciones solo aplican al equipo interno; clientes y freelance ven la lista plana
    const useSections =
      user?.role === "superadmin" ||
      user?.role === "agency_admin" ||
      user?.role === "consultant";

    if (!useSections) {
      return {
        menuSections: [{ id: "all", title: null, items: availableMenuItems }],
        showSectionHeaders: false,
      };
    }

    const sectionedIds = new Set(MENU_SECTIONS.flatMap((section) => section.itemIds));
    const sections = MENU_SECTIONS.map((section) => ({
      ...section,
      items: section.itemIds
        .map((id) => availableMenuItems.find((item) => item.id === id))
        .filter(Boolean),
    }));
    // Cualquier item sin sección asignada va a Operación para no perderlo
    const orphanItems = availableMenuItems.filter((item) => !sectionedIds.has(item.id));
    sections.find((section) => section.id === "operacion").items.push(...orphanItems);

    return {
      menuSections: sections.filter((section) => section.items.length > 0),
      showSectionHeaders: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMenuItems, user?.role]);

  // Cabecera de sección (texto rosa en mayúsculas, o logo SERSEO)
  const renderSectionHeader = (section) => {
    if (!showSectionHeaders || (!section.title && !section.isSerseo)) {
      return null;
    }

    if (isSidebarCollapsed) {
      return (
        <Box
          sx={{
            height: "1px",
            backgroundColor: neutrals.gray[200],
            margin: "8px 8px",
          }}
        />
      );
    }

    if (section.isSerseo) {
      return (
        <Box
          sx={{
            margin: "14px 8px 6px 8px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Box sx={{ flex: 1, height: "1px", backgroundColor: neutrals.gray[200] }} />
          <Box
            component="img"
            src={serseoImage}
            alt="Zona SERSEO"
            sx={{ height: "auto", maxHeight: "13px", flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, height: "1px", backgroundColor: neutrals.gray[200] }} />
        </Box>
      );
    }

    return (
      <Typography
        data-welcome-tour={WELCOME_TOUR_TARGETS[`section-${section.id}`]}
        sx={{
          padding: "24px 12px 6px 12px",
          fontSize: "14px",
          fontWeight: 400,
          color: colors.nav.section,
          lineHeight: 1.2,
        }}
      >
        {section.title}
      </Typography>
    );
  };

  // Menú flotante con los hijos de un grupo para navegar sin desplegarlo
  const withSubItemsHoverMenu = (node, item) => (
    <Tooltip
      placement="right-start"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${neutrals.gray[200]}`,
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            padding: "6px",
            minWidth: 180,
          },
        },
      }}
      title={
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {item.subItems
            .filter((subItem) => !subItem.disabled)
            .map((subItem) => {
              const SubIcon = subItem.icon;
              const isActive = isSubItemPathActive(item.id, subItem.path);
              return (
                <Box
                  key={subItem.id}
                  component={Link}
                  to={subItem.path}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "6px 10px",
                    borderRadius: "6px",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? colors.brand.pink : colors.nav.text,
                    "&:hover": { backgroundColor: neutrals.gray[50] },
                  }}
                >
                  {SubIcon && <SubIcon size={14} />}
                  {subItem.title}
                </Box>
              );
            })}
        </Box>
      }
    >
      {node}
    </Tooltip>
  );

  // Función helper para renderizar items del menú (reutilizable)
  const renderMenuItem = (item, marginBottom = "2px") => {
    const Icon = item.icon;

    // Si el item es colapsable (dropdown)
    if (item.isCollapsible && item.subItems) {
      const isOpen = openCollapsibles.has(item.id);
      const isAnySubItemActive = item.subItems.some(
        (subItem) => subItem.path && isSubItemPathActive(item.id, subItem.path)
      );
      const firstEnabledSubItem = item.subItems.find((subItem) => !subItem.disabled);

      return (
        <Collapsible key={item.id}>
          {withSubItemsHoverMenu(
          <SidebarMenuItem
            data-welcome-tour={WELCOME_TOUR_TARGETS[item.id]}
            sx={{
              marginBottom,
              borderRadius: "10px",
              textAlign: "left",
              backgroundColor: isAnySubItemActive
                ? colors.secondary.main
                : "transparent",
              "&:hover": {
                backgroundColor: isAnySubItemActive
                  ? colors.secondary.main
                  : neutrals.gray[50],
              },
              height: "34px",
            }}
          >
            <CollapsibleTrigger
              onClick={() => {
                const wasOpen = isOpen;

                // Comprimido: expandir el sidebar y dejar abierto este grupo
                if (isSidebarCollapsed) {
                  setSidebarOpen(true);
                  setOpenCollapsibles(new Set([item.id]));
                  if (!wasOpen && firstEnabledSubItem) {
                    navigate(firstEnabledSubItem.path);
                  }
                  return;
                }

                setOpenCollapsibles(prev => {
                  const newSet = new Set(prev);
                  if (wasOpen) {
                    newSet.delete(item.id);
                  } else {
                    newSet.add(item.id);
                  }
                  return newSet;
                });

                if (!wasOpen && firstEnabledSubItem) {
                  navigate(firstEnabledSubItem.path);
                }
              }}
            >
              <Box
                sx={{
                  height: "34px",
                  padding: isSidebarCollapsed ? "8px 0" : "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isSidebarCollapsed
                    ? "center"
                    : "space-between",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginLeft: isSidebarCollapsed ? 0 : "4px",
                  }}
                >
                  <Icon
                    size={16}
                    color={isAnySubItemActive ? colors.brand.pink : colors.nav.icon}
                  />
                  {!isSidebarCollapsed && (
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: isAnySubItemActive ? 600 : 400,
                      fontSize: "14px",
                      color: isAnySubItemActive
                        ? colors.brand.pink
                        : colors.nav.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.title}
                  </Typography>
                  )}
                </Box>
                {!isSidebarCollapsed && (
                  <ChevronDown
                    size={14}
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                )}
              </Box>
            </CollapsibleTrigger>
          </SidebarMenuItem>,
          item
          )}

          <CollapsibleContent open={isOpen && !isSidebarCollapsed}>
            <Box sx={{ paddingLeft: "20px", marginTop: "2px" }}>
              {item.subItems.map((subItem) => {
                const SubIcon = subItem.icon;
                const isSubItemActive =
                  !subItem.disabled && isSubItemPathActive(item.id, subItem.path);

                return (
                  <SidebarMenuItem
                    key={subItem.id}
                    sx={{
                      marginBottom: "2px",
                      borderRadius: "8px",
                      textAlign: "left",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "2px",
                        backgroundColor: isSubItemActive
                          ? colors.brand.pink
                          : neutrals.gray[300],
                        borderRadius: "1px",
                        transition: "background-color 0.2s",
                      },
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      height: "30px",
                    }}
                  >
                    <SidebarMenuButton
                      asChild
                      isActive={isSubItemActive}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Box
                        component={subItem.disabled ? "div" : Link}
                        to={subItem.disabled ? undefined : subItem.path}
                        aria-disabled={subItem.disabled || undefined}
                        style={{
                          cursor: subItem.disabled ? "default" : "pointer",
                          opacity: subItem.disabled ? 0.65 : 1,
                          height: "30px",
                          padding: "6px 12px",
                          textDecoration: "none",
                          color: "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          {SubIcon && (
                            <SubIcon
                              size={16}
                              color={isSubItemActive ? colors.brand.pink : colors.nav.icon}
                            />
                          )}
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: isSubItemActive ? 600 : 400,
                              fontSize: "14px",
                              color: isSubItemActive
                                ? colors.brand.pink
                                : colors.nav.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {subItem.title}
                          </Typography>
                        </Box>
                        {subItem.badge === "beta" && (
                          <Badge
                            sx={{
                              background: colors.betaBadgeGradient,
                              color: "#fff",
                              fontSize: "0.55rem",
                              fontWeight: 600,
                              padding: "3px 6px",
                              borderRadius: "9px",
                              textTransform: "uppercase",
                              letterSpacing: "0.02em",
                              lineHeight: 1,
                              height: "auto",
                              minHeight: "unset",
                              alignSelf: "center",
                              border: "none",
                            }}
                          >
                            Beta
                          </Badge>
                        )}
                        {subItem.badge === "soon" && (
                          <Badge
                            sx={{
                              backgroundColor: neutrals.gray[100],
                              color: neutrals.gray[500],
                              fontSize: "0.55rem",
                              fontWeight: 600,
                              padding: "3px 6px",
                              borderRadius: "9px",
                              textTransform: "uppercase",
                              letterSpacing: "0.02em",
                              lineHeight: 1,
                              height: "auto",
                              minHeight: "unset",
                              alignSelf: "center",
                              border: "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Próximamente
                          </Badge>
                        )}
                      </Box>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </Box>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    // Item normal (no colapsable)
    const isActive =
      currentPath === item.path ||
      currentPath.startsWith(item.path + "/");
    const shouldDisableSetupNavigation =
      item.id === "setup" &&
      user?.role === "client" &&
      !isMultiClientUser &&
      currentPath.startsWith("/setup/");
    const isNavigationDisabled = shouldDisableSetupNavigation || item.comingSoon;
    const linkTarget = isNavigationDisabled
      ? currentPath
      : item.path;
    const wrapWithTooltip = item.comingSoon
      ? (node, _title, key) => (
        <Tooltip key={key} title="Próximamente" placement="right" arrow>
          {node}
        </Tooltip>
      )
      : withCollapsedTooltip;

    return wrapWithTooltip(
      <SidebarMenuItem
        key={item.id}
        data-welcome-tour={WELCOME_TOUR_TARGETS[item.id]}
        sx={{
          marginBottom,
          borderRadius: "10px",
          textAlign: "left",
          backgroundColor: isActive
            ? colors.secondary.main
            : "transparent",
          "&:hover": {
            backgroundColor: isActive
              ? colors.secondary.main
              : neutrals.gray[50],
          },
          height: "34px",
        }}
      >
        <SidebarMenuButton
          asChild
          isActive={isActive}
          sx={{
            width: "100%",
          }}
        >
          <Link
            to={linkTarget}
            onClick={(event) => {
              if (isNavigationDisabled) {
                event.preventDefault();
              }
            }}
            aria-disabled={item.comingSoon || undefined}
            style={{
              height: "34px",
              cursor: item.comingSoon ? "default" : undefined,
              opacity: item.comingSoon ? 0.65 : undefined,
              padding: isSidebarCollapsed ? "8px 0" : "8px 12px",
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: isSidebarCollapsed ? "center" : "space-between",
              width: "100%",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginLeft: isSidebarCollapsed ? 0 : "4px",
              }}
            >
              <Icon
                size={16}
                color={isActive ? colors.brand.pink : colors.nav.icon}
              />
              {!isSidebarCollapsed && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: isActive ? 600 : 400,
                  fontSize: "14px",
                  color: isActive
                    ? colors.brand.pink
                    : colors.nav.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.title}
              </Typography>
              )}
            </Box>
            {isSidebarCollapsed ? (
              item.badge && (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: colors.brand.pink,
                  }}
                />
              )
            ) : item.badge === "beta" ? (
              <Badge
                sx={{
                  background: colors.betaBadgeGradient,
                  color: "#fff",
                  fontSize: "0.55rem",
                  fontWeight: 600,
                  padding: "3px 6px",
                  borderRadius: "9px",
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                  lineHeight: 1,
                  height: "auto",
                  minHeight: "unset",
                  alignSelf: "center",
                  border: "none",
                }}
              >
                Beta
              </Badge>
            ) : item.badge ? (
              <Badge variant="destructive">{item.badge}</Badge>
            ) : null}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>,
      item.title,
      item.id
    );
  };

  return (
    <SidebarProvider open={sidebarOpen && !isSettingsRoute} onOpenChange={setSidebarOpen}>
      <Sidebar variant="docked" collapsible="icon">

        <SidebarContent>
          {/* Selector de Filtro en Sidebar */}
          {!isSidebarCollapsed &&
            (user?.role === "superadmin" ||
            user?.role === "agency_admin" ||
            user?.role === "consultant" ||
            shouldShowClientFilter) && (
              <Box
                sx={{
                  padding: "16px 12px 16px 8px",
                  borderBottom: `1px solid ${neutrals.gray[200]}`,
                }}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        backgroundColor: "transparent",
                        border: `1px solid ${neutrals.gray[200]}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        width: "100%",
                        "&:hover": {
                          backgroundColor: neutrals.gray[50],
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                          {showClientAvatars && selectedFilterOption ? (
                            <Avatar
                              size="sm"
                              name={selectedFilterOption.name}
                              src={getAvatarImageUrl(selectedFilterOption.image_url) || undefined}
                            />
                          ) : (
                            <Globe
                              size={14}
                              color={roleColors.primary || colors.primary.main}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedFilterData.name}
                        </Typography>
                      </Box>
                      <ChevronDown size={14} color={neutrals.gray[400]} />
                    </Box>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sx={{
                      minWidth: "240px",
                      borderRadius: "8px",
                      border: `1px solid ${neutrals.gray[200]}`,
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    onCloseAutoFocus={() => setFilterSearchQuery("")}
                  >
                    {/* Search Input */}
                    <Box
                      sx={{
                        padding: "12px",
                        borderBottom: `1px solid ${neutrals.gray[200]}`,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          backgroundColor: neutrals.gray[50],
                          borderRadius: "8px",
                          border: `1px solid ${neutrals.gray[200]}`,
                        }}
                      >
                        <Search size={16} color={neutrals.gray[400]} />
                        <input
                          type="text"
                          placeholder={
                            user?.role === "superadmin"
                              ? "Buscar agencia..."
                              : isMultiClientUser
                                ? "Buscar empresa..."
                                : "Buscar cliente..."
                          }
                          value={filterSearchQuery}
                          onChange={(e) => setFilterSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{
                            border: "none",
                            outline: "none",
                            backgroundColor: "transparent",
                            fontSize: "13px",
                            fontFamily: "inherit",
                            color: theme.palette.text.primary,
                            width: "100%",
                          }}
                        />
                      </Box>
                    </Box>
                    {/* Options List */}
                    <Box sx={{ overflowY: "auto", maxHeight: "min(400px, 60vh)", padding: "4px 0" }}>
                      {/* Vista Global - solo visible si no hay búsqueda o coincide */}
                      {(!filterSearchQuery || "vista global".includes(filterSearchQuery.toLowerCase())) && (
                        <DropdownMenuItem
                          onClick={() => {
                            changeSelectedFilter("global");
                            if (location.pathname.startsWith("/conversations/")) {
                              navigate("/conversations");
                            }
                          }}
                          sx={{
                            backgroundColor:
                              selectedFilterData.id === "global"
                                ? colors.brand.soft
                                : "transparent",
                            borderRadius: "6px",
                            margin: "2px 0",
                            padding: "8px 12px",
                            "&:hover": {
                              backgroundColor: neutrals.gray[50],
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              width: "100%",
                            }}
                          >
                            <Globe
                              size={14}
                              color={roleColors.primary || colors.primary.main}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                              }}
                            >
                              Vista Global
                            </Typography>
                          </Box>
                        </DropdownMenuItem>
                      )}
                      {filterOptions
                        .filter((item) =>
                          item.name.toLowerCase().includes(filterSearchQuery.toLowerCase()) ||
                          (item.contact_name && item.contact_name.toLowerCase().includes(filterSearchQuery.toLowerCase()))
                        )
                        .map((item) => (
                          <DropdownMenuItem
                            key={item.id}
                            onClick={() => {
                              changeSelectedFilter(item.id);
                              if (
                                location.pathname.startsWith("/conversations/") &&
                                user.role === "superadmin"
                              ) {
                                navigate("/conversations");
                              }
                            }}
                            sx={{
                              backgroundColor:
                                selectedFilterData.id === item.id
                                  ? colors.brand.soft
                                  : "transparent",
                              borderRadius: "6px",
                              margin: "2px 0",
                              padding: "8px 12px",
                              "&:hover": {
                                backgroundColor: neutrals.gray[50],
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                width: "100%",
                              }}
                            >
                              {showClientAvatars && (
                                <Avatar
                                  size="sm"
                                  name={item.name}
                                  src={getAvatarImageUrl(item.image_url) || undefined}
                                  sx={{ flexShrink: 0 }}
                                />
                              )}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "13px",
                                      color: item.is_read_only
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.secondary,
                                      lineHeight: 1.2,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.name}
                                  </Typography>
                                  {item.is_read_only && <ReadOnlyBadge />}
                                  {item.is_archived && (
                                    <Box
                                      component="span"
                                      title="Archivada"
                                      aria-label="Archivada"
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        backgroundColor: theme.palette.error.light,
                                      }}
                                    />
                                  )}
                                </Box>
                                {(user?.role === "agency_admin" ||
                                  user?.role === "consultant") &&
                                  item.contact_name && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: neutrals.gray[500],
                                        fontSize: "12px",
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {item.contact_name}
                                    </Typography>
                                  )}
                              </Box>
                            </Box>
                          </DropdownMenuItem>
                        ))}
                      {/* No results message */}
                      {filterSearchQuery &&
                        !("vista global".includes(filterSearchQuery.toLowerCase())) &&
                        filterOptions.filter((item) =>
                          item.name.toLowerCase().includes(filterSearchQuery.toLowerCase()) ||
                          (item.contact_name && item.contact_name.toLowerCase().includes(filterSearchQuery.toLowerCase()))
                        ).length === 0 && (
                          <Box sx={{ padding: "12px 16px" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                fontStyle: "italic",
                                color: neutrals.gray[500],
                              }}
                            >
                              No se encontraron resultados
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Box>
            )}

          <SidebarMenu
            sx={{
              padding: isSidebarCollapsed ? "8px 8px" : "8px 12px 12px 8px",
            }}
          >
            {menuSections.map((section) => (
              <React.Fragment key={section.id}>
                {renderSectionHeader(section)}
                {section.items.map((item) => renderMenuItem(item))}
              </React.Fragment>
            ))}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter>
          <Box
            sx={{
              padding: isSidebarCollapsed ? "12px 8px" : "12px 12px 12px 8px",
            }}
          >
            <UserBox
              profileImage={user?.profile_image}
              compact={isSidebarCollapsed}
            />
          </Box>
        </SidebarFooter>
      </Sidebar>
      {showSettingsSidebar && <SettingsSidebar />}

      <SidebarInset
        docked
        sx={{
          ...(showSettingsSidebar && { paddingLeft: `${DOCKED_SIDEBAR_WIDTH_ICON + DOCKED_SIDEBAR_WIDTH}px` }),
          display: "flex",
          // En móvil scrollea el documento para que funcione el pull-to-refresh
          height: { xs: "auto", md: "100vh" },
          minHeight: { xs: "100dvh", md: "auto" },
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          overflowX: { xs: "clip", md: "hidden" },
          overflowY: { xs: "visible", md: "auto" },
          backgroundColor: theme.palette.background.accent,
        }}
      >
        {renderAppHeader({
          pageContext,
          user,
          notificationsCount,
          onConversationNotificationClick: refreshConversationsNotifications,
          theme,
          neutrals,
          isMobile: isMobileViewport,
          agencyLogoUrl,
        })}
        <CommercialTour updateUiPreferences={updateUiPreferences} onClientCreated={refreshFilterData} />

        <Box
          component="main"
          sx={{
            flex: 1,
            padding: { xs: "16px", md: "24px" },
            backgroundColor: theme.palette.background.accent,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowX: { xs: "clip", md: "hidden" },
            overflowY: { xs: "visible", md: "auto" },
          }}
        >
          <BillingStatusBanner />
          <PageHeader
            title={pageContext.title}
            subtitle={pageContext.subtitle}
            showTitle={pageContext.showTitle !== false}
            leftContent={pageContext.leftContent}
            rightContent={pageContext.rightContent}
            titleRightContent={pageContext.titleRightContent}
          />
          {selectedFilterOption?.is_read_only && (
            <AlertNote icon={LockKeyhole} sx={{ mb: 2 }}>
              {readOnlyClientMessage}
            </AlertNote>
          )}
          <Outlet
            key={outletKey}
            context={{
              setPageContext,
              showTitle: pageContext.showTitle !== false,
              selectedFilter:
                selectedFilterData.id === "global"
                  ? null
                  : selectedFilterData.id,
              selectedFilterData,
              changeSelectedFilter,
              userRole: user?.role,
              refreshFilterData,
              userRefreshKey,
              filterRefresh,
              refreshFilterKey,
              dashboardRefreshKey,
              clientsRefreshKey,
              setClientsRefreshKey,
              clientDetailRefreshKey,
              setClientDetailRefreshKey,
              teamRefreshKey,
              projectsRefreshKey,
              onboardingRefreshKey,
              conversationsRefreshKey,
              conversationsNotificationsRefreshKey,
              incidentsRefreshKey,
              technicalSetupRefreshKey,
              setTechnicalSetupRefreshKey,
              reportsRefreshKey,
              Clientes_prop,
              filterOptions,
              leadsRefreshKey,
              contentGeneratorRefreshKey,
              marketingPlanRefresh,
              setMarketingPlanRefresh,
              clientIds,
              isMultiClientUser,
              updateUiPreferences,
              selectedClientSetupActive,
              selectedClientSetupNotNeeded,
              selectedClientReadOnly: Boolean(selectedFilterOption?.is_read_only),
            }}
          />
        </Box>

        <AnnouncementDetailModal
          open={isAnnouncementModalOpen && orderedPendingAnnouncements.length > 0}
          onClose={handleCloseAnnouncementsCarousel}
          announcements={orderedPendingAnnouncements}
          initialIndex={announcementCarouselIndex}
          onIndexChange={setAnnouncementCarouselIndex}
          userRole={user?.role}
        />
        <SuccessPopup
          isOpen={announcementCloseErrorPopupOpen}
          onClose={() => setAnnouncementCloseErrorPopupOpen(false)}
          message="Para cerrar esta vista necesitas leer todos los nuevos anuncios"
          error
          autoHideDuration={6000}
        />
      </SidebarInset>
    </SidebarProvider>
  );
}
